import _ from 'lodash';
import {ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {updateEntireProductState, updateProductState} from '~/lib/product';
import {MergeAction} from '~/redux/actions/MergeActions';
import {ProductStorePresenceAttributes, ProductsPresenceAcrossStores} from '~/utils/ProductAPI';

export interface MergeState {
    exactMatchedFields: ProductField[];
    mergingToOrgProduct: boolean;
    productToMergeToStorePresence:
        | Partial<ProductStorePresenceAttributes>[]
        | ProductStorePresenceAttributes[];
    productFormDirty: boolean;
    productFieldSources: Partial<Record<ProductField, Partial<ProductInCatalog>>>;
    productToMergeTo: Partial<ProductInCatalog> | ProductInCatalog;
    storePresence: Partial<ProductsPresenceAcrossStores> | ProductsPresenceAcrossStores;
}

const {
    CLEAR_FORM,
    CLEAR_STATE,
    SET_MERGE_TO_PRODUCT,
    SET_STORE_PRESENCE,
    UPDATE_PRODUCT,
    UPDATE_PRODUCT_SOURCE,
    REMOVE_PRODUCT_SOURCE,
    REMOVE_PRODUCT_SOURCE_FROM_ALL,
    SET_ALL_PRODUCT_SOURCES,
    UPDATE_EXACT_MATCHES,
} = MergeAction;

const DEFAULT_MERGE_STATE: MergeState = {
    exactMatchedFields: [],
    mergingToOrgProduct: false,
    productFieldSources: {},
    productFormDirty: false,
    productToMergeTo: {},
    productToMergeToStorePresence: [],
    storePresence: {},
};

export const MergeReducer = (state = DEFAULT_MERGE_STATE, action: any) => {
    const {type: typeOfAction, value} = action;

    switch (typeOfAction) {
        case CLEAR_STATE:
            return DEFAULT_MERGE_STATE;
        case CLEAR_FORM:
            return {
                ...state,
                productToMergeTo: {},
            };
        case UPDATE_PRODUCT:
            const {field, value: inputValue} = value;

            return {
                ...state,
                productFormDirty: true,
                productToMergeTo: updateProductState(field, inputValue, state.productToMergeTo),
            };
        case UPDATE_PRODUCT_SOURCE:
            const {field: fieldToUpdate, value: sourceProduct} = value;

            const productFieldSources = {
                ...state.productFieldSources,
                [fieldToUpdate]: sourceProduct,
            };

            const newValue = sourceProduct[fieldToUpdate];
            return {
                ...state,
                productFieldSources,
                productToMergeTo: updateProductState(
                    fieldToUpdate,
                    newValue,
                    state.productToMergeTo
                ),
            };
        case REMOVE_PRODUCT_SOURCE:
            const {field: fieldToRemove} = value;

            return {
                ...state,
                productFieldSources: _.omit(state.productFieldSources, [fieldToRemove]),
            };
        case REMOVE_PRODUCT_SOURCE_FROM_ALL:
            const currentSources = {...state.productFieldSources};
            for (const productField in currentSources) {
                if (currentSources[productField] === value) {
                    delete currentSources[productField];
                }
            }

            return {
                ...state,
                productFieldSources: currentSources,
            };
        case SET_ALL_PRODUCT_SOURCES:
            const sources = {};
            const product = value;

            for (const productField in product) {
                sources[productField] = product;
            }

            return {
                ...state,
                productFieldSources: sources,
                productToMergeTo: updateEntireProductState(value, state.productToMergeTo),
            };
        case UPDATE_EXACT_MATCHES:
            return {
                ...state,
                exactMatchedFields: value,
            };
        case SET_MERGE_TO_PRODUCT:
            let mergingToOrgProduct = false;
            let productToMergeToStorePresence = [] as
                | ProductStorePresenceAttributes[]
                | Partial<ProductStorePresenceAttributes>[];

            const productToMergeToId = _.get(value, 'id');

            if (productToMergeToId != null) {
                mergingToOrgProduct = true;
                productToMergeToStorePresence = _.get(
                    state.storePresence,
                    productToMergeToId,
                    []
                ) as ProductStorePresenceAttributes[] | Partial<ProductStorePresenceAttributes>[];
            }
            return {
                ...state,
                mergingToOrgProduct,
                productToMergeToStorePresence,
                productFormDirty: true,
                productToMergeTo: value,
            };
        case SET_STORE_PRESENCE:
            return {
                ...state,
                storePresence: value,
            };
        default:
            return state;
    }
};

export default MergeReducer;
